import { CleaveTextField } from '@/global/components/Form/CleaveTextField'
import { cn } from '@/lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import 'cleave.js/dist/addons/cleave-phone.br'
import type { CleaveOptions } from 'cleave.js/options'
import type { Props as CleaveProps } from 'cleave.js/react/props'
import * as React from 'react'

/**
 * Caixa do campo, com a geometria do `filled` do MUI (56px, rótulo 12px sobre valor 16px).
 * Underline é `after:` absoluto, não `border-b`: a troca 1px → 2px não desloca o layout.
 */
const inputVariants = cva(
    [
        // Gatilho de select/combobox é focável: sem `outline-none` o browser desenha o anel por cima do underline.
        'group/campo relative isolate flex w-full text-foreground outline-none',
        'transition-[color,background-color,border-color,box-shadow]',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-disabled',
    ],
    {
        variants: {
            variant: {
                filled: [
                    'rounded-sm border-0 px-3 rounded-b-none',
                    'bg-muted/80 hover:bg-muted/95 focus-within:bg-muted/95',
                    "after:pointer-events-none after:absolute after:content-['']",
                    'after:inset-x-0 after:bottom-0 after:h-px',
                    'after:bg-foreground/40 hover:after:bg-foreground',
                    'after:transition-[height,background-color]',
                    'focus-within:after:h-1 focus-within:after:bg-primary',
                    'focus-within:hover:after:bg-primary',
                    'data-[invalid]:after:h-0.5 data-[invalid]:after:bg-destructive',
                    'data-[invalid]:hover:after:bg-destructive',
                ],
                outlined: [
                    'rounded-sm px-3 shadow-xs',
                    'border border-input bg-transparent dark:bg-input/30',
                    'focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50',
                    'data-[invalid]:border-destructive data-[invalid]:ring-destructive/20',
                    'dark:data-[invalid]:ring-destructive/40',
                ],
            },
            comRotulo: {
                true: 'flex-col justify-center',
                false: 'items-center',
            },
            /** 56px (`medium`) ou 48px (`small`). */
            size: {
                medium: '',
                small: '',
            },
        },
        compoundVariants: [
            {
                variant: 'filled',
                comRotulo: true,
                size: 'medium',
                class: 'h-14 pt-[7px] pb-2 text-base',
            },
            {
                variant: 'filled',
                comRotulo: true,
                size: 'small',
                class: 'h-12 py-1 text-base',
            },
            {
                variant: 'filled',
                comRotulo: false,
                size: 'medium',
                class: 'h-14 text-base',
            },
            {
                variant: 'filled',
                comRotulo: false,
                size: 'small',
                class: 'h-12 text-base',
            },
            {
                variant: 'outlined',
                comRotulo: true,
                size: 'medium',
                class: 'h-14 py-2 text-base',
            },
            {
                variant: 'outlined',
                comRotulo: true,
                size: 'small',
                class: 'h-12 py-1 text-base',
            },
            // Mesma altura do `Button` (`h-10`), para barras de ferramentas.
            {
                variant: 'outlined',
                comRotulo: false,
                size: 'medium',
                class: 'h-10 text-sm',
            },
            {
                variant: 'outlined',
                comRotulo: false,
                size: 'small',
                class: 'h-9 text-sm',
            },
        ],
        defaultVariants: {
            variant: 'filled',
            comRotulo: false,
            size: 'medium',
        },
    },
)

/**
 * Rótulo dentro da caixa; cor segue o estado do campo via grupo `campo`.
 * `shrink-0` obrigatório: `truncate` zera o mínimo flex e um adorno alto faria o rótulo sumir.
 */
const rotuloCampoClass = [
    'max-w-full shrink-0 truncate text-xs leading-[17.25px] font-normal select-none',
    'origin-left text-muted-foreground',
    // Tailwind 4 emite `translate`/`scale` individuais, não `transform`.
    'transition-[color,max-width,transform,translate,scale] duration-200 ease-out',
    'group-focus-within/campo:text-primary',
    'group-data-[invalid]/campo:text-destructive',
]

/**
 * Rótulo no lugar do valor (campo vazio, sem foco nem placeholder): 12px × 1.3333 = 16px.
 * `translate`+`scale` não refluem a caixa; `max-w-[75%]` × 1.3333 = 100% evita vazar.
 */
const rotuloGrandeClass =
    'max-w-[75%] translate-y-3 scale-[1.3333] text-foreground'

/**
 * {@link rotuloGrandeClass} decidido em CSS, pois o controle pode ser não controlado.
 * Só vale sem placeholder — ver `rotuloFlutua`.
 */
const rotuloGrandeCssClass = [
    'group-[:not(:focus-within):has(:placeholder-shown)]/campo:max-w-[75%]',
    'group-[:not(:focus-within):has(:placeholder-shown)]/campo:translate-y-3',
    'group-[:not(:focus-within):has(:placeholder-shown)]/campo:scale-[1.3333]',
    'group-[:not(:focus-within):has(:placeholder-shown)]/campo:text-foreground',
]

/** Tipos com placeholder nativo (`dd/mm/aaaa`, `--:--`): rótulo nasce encolhido. */
const tiposComPlaceholderProprio = new Set([
    'date',
    'time',
    'month',
    'week',
    'datetime-local',
])

/**
 * Linha do valor (adornos + controle). `min-h-[23px]` deixa adorno alto ceder sem sumir a linha vazia.
 * `z-10` mantém adornos acima do rótulo transformado; o `isolate` da caixa contém o z-index.
 */
const linhaControleClass =
    'relative z-10 flex w-full min-h-[23px] min-w-0 items-center gap-2 leading-[23px]'

const adornmentClass = 'flex shrink-0 items-center text-muted-foreground'

/**
 * Realce do foco para gatilhos cujo foco vai para o popover aberto.
 * `hover:` incluso: popover não modal deixa o gatilho receber hover.
 */
const realceCampoAberto = {
    filled: 'bg-muted/95 after:h-0.5 after:bg-primary hover:after:bg-primary',
    outlined: 'border-ring ring-[3px] ring-ring/50',
} as const

/** Máscara implícita do `type="tel"` (telefone BR, com e sem nono dígito). */
const MASCARA_TELEFONE: CleaveOptions = { phone: true, phoneRegionCode: 'BR' }

const controlClass = [
    'w-full min-w-0 flex-1 bg-transparent',
    'text-inherit placeholder:select-none',
    'border-none outline-none',
    'placeholder:text-muted-foreground',
    'selection:bg-primary selection:text-primary-foreground',
    'disabled:cursor-not-allowed',
]

type CamposComuns = VariantProps<typeof inputVariants> & {
    /** Rótulo dentro da caixa, acima do valor. */
    label?: React.ReactNode
    /** Conteúdo à esquerda do campo (substitui o `InputAdornment` do MUI). */
    startAdornment?: React.ReactNode
    /** Conteúdo à direita do campo (substitui o `InputAdornment` do MUI). */
    endAdornment?: React.ReactNode
    /** Máscara do cleave.js; troca o `<input>` por {@link CleaveTextField}. `type="tel"` já tem uma. */
    options?: CleaveOptions
    /**
     * Clique na caixa inteira, não só no controle (como o root do `TextField` do MUI).
     * Botões dentro do campo precisam de `stopPropagation()` para não disparar ambos.
     */
    onClick?: React.MouseEventHandler<HTMLDivElement>
}

/** Sem `size` (vira variante de altura) e `onClick` (redeclarado no root). */
type PropsElemento<E extends 'input' | 'textarea'> = Omit<
    React.ComponentPropsWithoutRef<E>,
    'size' | 'onClick'
>

type InputProps =
    | (CamposComuns & PropsElemento<'input'> & { multiline?: false })
    | (CamposComuns & PropsElemento<'textarea'> & { multiline: true })

/**
 * Campo de texto; `className` e `onClick` vão no wrapper (use `[&_input]:…` para o controle).
 * A `ref` aponta para o `<input>`/`<textarea>`, usada pelo react-hook-form.
 */
const Input = React.forwardRef<
    HTMLInputElement | HTMLTextAreaElement,
    InputProps
>(
    (
        {
            className,
            variant,
            size,
            label,
            startAdornment,
            endAdornment,
            onClick,
            ...props
        },
        ref,
    ) => {
        const { multiline, options, ...resto } = props as InputProps & {
            multiline?: boolean
        }
        const ariaInvalid = resto['aria-invalid']
        const invalid =
            ariaInvalid !== undefined &&
            ariaInvalid !== false &&
            ariaInvalid !== 'false'
        const temRotulo = label !== undefined && label !== ''
        const tipo = (resto as { type?: string }).type
        const temPlaceholderProprio =
            tipo !== undefined && tiposComPlaceholderProprio.has(tipo)
        const temPlaceholder =
            typeof resto.placeholder === 'string' && resto.placeholder !== ''
        // Sem placeholder, injeta `' '` para o CSS detectar vazio via `:placeholder-shown`.
        const rotuloFlutua =
            temRotulo && !temPlaceholder && !temPlaceholderProprio
        const mascara =
            options ?? (tipo === 'tel' ? MASCARA_TELEFONE : undefined)
        const propsControle = rotuloFlutua
            ? { ...resto, placeholder: ' ' }
            : resto

        return (
            <div
                data-slot="input"
                data-invalid={invalid || undefined}
                data-disabled={resto.disabled || undefined}
                onClick={onClick}
                className={cn(
                    inputVariants({ variant, size, comRotulo: temRotulo }),
                    // Altura do multiline vem do `rows`; `min-h` fixo brigaria com ele.
                    multiline && 'h-auto items-start py-2',
                    className,
                )}
            >
                {temRotulo && (
                    <label
                        data-slot="input-label"
                        htmlFor={resto.id}
                        className={cn(
                            rotuloCampoClass,
                            rotuloFlutua && rotuloGrandeCssClass,
                        )}
                    >
                        {label}
                    </label>
                )}
                <div
                    className={cn(
                        linhaControleClass,
                        multiline && 'items-start',
                    )}
                >
                    {startAdornment && (
                        <span
                            data-slot="input-start-adornment"
                            className={cn(adornmentClass, 'mt-1')}
                        >
                            {startAdornment}
                        </span>
                    )}
                    {multiline ? (
                        <textarea
                            // Padrão; `rows` do call-site sobrescreve no espalhamento.
                            rows={3}
                            ref={ref as React.Ref<HTMLTextAreaElement>}
                            data-slot="input-control"
                            className={cn(controlClass, 'resize-y')}
                            {...(propsControle as PropsElemento<'textarea'>)}
                        />
                    ) : mascara ? (
                        // Cast: `onChange` do cleave difere do `ChangeEventHandler`.
                        // `data-slot` fica fora: `Props` não tem index signature.
                        <CleaveTextField
                            ref={ref as React.Ref<HTMLInputElement>}
                            data-slot="input-control"
                            className={cn(controlClass)}
                            {...({
                                options: mascara,
                                ...propsControle,
                            } as CleaveProps)}
                        />
                    ) : (
                        <input
                            ref={ref as React.Ref<HTMLInputElement>}
                            data-slot="input-control"
                            className={cn(controlClass)}
                            {...(propsControle as PropsElemento<'input'>)}
                        />
                    )}
                    {endAdornment && (
                        <span
                            data-slot="input-end-adornment"
                            className={cn(adornmentClass, 'mb-2')}
                        >
                            {endAdornment}
                        </span>
                    )}
                </div>
            </div>
        )
    },
)
Input.displayName = 'Input'

export {
    Input,
    inputVariants,
    linhaControleClass,
    realceCampoAberto,
    rotuloCampoClass,
    rotuloGrandeClass,
}
export type { InputProps }
